import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "@/hooks/useApi";
import { useNow } from "@/hooks/useNow";
import { useAuth } from "@/components/feature/AuthProvider";
import type { RpsBrief } from "@/lib/types";
import { SkeletonCard } from "@/components/base/Skeleton";
import RpsCard from "@/pages/rps/components/RpsCard";
import RpsWriteModal from "@/pages/rps/components/RpsWriteModal";
import GameSectionEmpty from "@/components/feature/GameSectionEmpty";
import type { GamePastItem } from "@/components/feature/GamePastStrip";

// 메인 홈 «가위바위보» 섹션.
// 열린 판이 없어도 섹션은 항상 보이며,
// 빈 상태일 땐 왼쪽 안내+등록 버튼, 오른쪽 지난 결과 썸네일 몇 개만 간단히 표시한다.
export default function RpsSection() {
  const { data, loading, serverTs, reload } = useApi<RpsBrief[]>("rps.php");
  const now = useNow();
  const { member, requireLogin } = useAuth();
  const [writeOpen, setWriteOpen] = useState(false);

  const list = data ?? [];
  const { open, past } = useMemo(
    () => ({
      open: list.filter((r) => r.state === "open"),
      past: list.filter((r) => r.state !== "open"),
    }),
    [data]
  );

  const elapsed = serverTs ? Math.max(0, now / 1000 - serverTs) : 0;
  const scroll = open.length >= 3;

  const openWrite = () => {
    if (!member) {
      requireLogin("/rps");
      return;
    }
    setWriteOpen(true);
  };

  const handleCreated = (no?: number) => {
    setWriteOpen(false);
    reload();
    if (no) {
      window.setTimeout(() => {
        document
          .getElementById(`rps-${no}`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 350);
    }
  };

  const pastItems: GamePastItem[] = past.slice(0, 5).map((r) => ({
    no: r.no,
    to: `/rps/${r.no}`,
    icon: "✊",
    title: r.subject,
    meta: `이긴 사람 ${r.win_cnt ?? 0}명`,
  }));

  return (
    <section className="w-full bg-background-50 px-4 py-14 md:px-6 md:py-16">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              <span aria-hidden="true">✊✌️🖐️</span>
              가위바위보
            </h2>
            <p className="mt-2 text-foreground-600">
              회원이 여는 가벼운 게임. 마감되면 한꺼번에 공개됩니다.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={openWrite}
              className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
            >
              <i className="ri-add-line text-base" aria-hidden="true"></i>
              판 열기
            </button>
            <Link
              to="/rps"
              className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-primary-200 bg-primary-50 px-5 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-100"
            >
              전체 보기
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : open.length === 0 ? (
          <GameSectionEmpty
            icon="ri-gamepad-line"
            emptyTitle="지금 열린 판이 없습니다."
            emptyDesc="첫 번째 판을 열어보세요!"
            registerLabel="판 열기"
            onRegister={openWrite}
            pastItems={pastItems}
            pastTitle="지난 결과"
            pastEmptyText="아직 마감된 판이 없습니다."
          />
        ) : scroll ? (
          <div className="-mx-4 flex gap-5 overflow-x-auto px-4 pb-2 md:mx-0 md:px-0">
            {open.map((r) => (
              <div key={r.no} className="w-[320px] shrink-0">
                <RpsCard brief={r} elapsed={elapsed} />
              </div>
            ))}
          </div>
        ) : (
          <div
            className={`grid grid-cols-1 gap-5 ${
              open.length > 1 ? "md:grid-cols-2" : ""
            }`}
          >
            {open.map((r) => (
              <RpsCard key={r.no} brief={r} elapsed={elapsed} />
            ))}
          </div>
        )}
      </div>

      <RpsWriteModal
        open={writeOpen}
        onClose={() => setWriteOpen(false)}
        onCreated={handleCreated}
      />
    </section>
  );
}